(() => {
  const elementName = 'pm-home-reasons-carousel';
  if (customElements.get(elementName)) return;

  class PMHomeReasonsCarousel extends HTMLElement {
    connectedCallback() {
      this.teardown();
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.viewport = this.querySelector('[data-pm-home-reasons-viewport]');
      this.track = this.querySelector('[data-pm-home-reasons-track]');
      this.slides = [...this.querySelectorAll('[data-pm-home-reason-slide]')]
        .filter((node) => node.closest(elementName) === this);
      this.dots = [...this.querySelectorAll('[data-pm-home-reason-dot]')]
        .filter((node) => node.closest(elementName) === this);
      this.status = this.querySelector('[data-pm-home-reasons-status]');
      this.canvas = this.querySelector('.pm-home-reasons__canvas');
      this.dotRow = this.querySelector('.pm-home-reasons__dots');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.desktopQuery = window.matchMedia('(min-width: 915px)');
      this.index = 0;
      this.dragX = 0;
      this.dragging = false;

      if (!this.viewport || !this.track || this.slides.length !== 6 || this.dots.length !== 6) {
        this.dataset.controllerReady = 'false';
        return;
      }

      // The CSS native-scroll fallback may have been used before upgrade;
      // clear any residual offset before the transform-driven track takes over.
      this.viewport.scrollLeft = 0;
      // Native image dragging must not cancel the carousel pointer gesture.
      this.slides.forEach(slide => slide.querySelectorAll('img').forEach(image => { image.draggable = false; }));
      this.dataset.controllerReady = 'true';

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.go(Number(dot.dataset.pmHomeReasonDot), { announce: true });
        }, { signal: this.signal });
        dot.addEventListener('keydown', (event) => this.onDotKeydown(event), { signal: this.signal });
      });

      this.viewport.addEventListener('keydown', (event) => this.onKeydown(event), { signal: this.signal });
      this.unbindSwipe = window.PmSwipe?.bind(this.viewport, {
        threshold: 30,
        onHorizontalLock: () => {
          this.dragging = true;
          this.viewport.dataset.dragging = 'true';
          this.render({ immediate: true });
        },
        onDrag: (deltaX) => {
          this.dragX = deltaX;
          if ((this.index === 0 && this.dragX > 0) || (this.index === this.maxIndex && this.dragX < 0)) this.dragX *= .28;
          this.render({ immediate: true, dragOffset: this.dragX });
        },
        onCommit: (direction) => {
          this.endDrag();
          this.go(this.index + direction, { announce: true });
        },
        onCancel: () => {
          this.endDrag();
          this.render({ immediate: false });
        }
      });

      document.addEventListener('shopify:section:load', (event) => {
        if (this.matchesEditorEvent(event)) this.go(0, { immediate: true, announce: false });
      }, { signal: this.signal });

      document.addEventListener('shopify:block:select', (event) => {
        if (!this.matchesEditorEvent(event)) return;
        const blockId = event.detail?.blockId || event.target?.dataset?.blockId;
        const index = this.slides.findIndex((slide) => slide.dataset.pmHomeReasonBlockId === blockId);
        if (index >= 0) this.go(index, { immediate: true, announce: false });
      }, { signal: this.signal });

      this.viewportWidth = this.viewport.getBoundingClientRect().width;
      this.resizeObserver = new ResizeObserver(() => {
        const width = this.viewport.getBoundingClientRect().width;
        const widthChanged = width !== this.viewportWidth;
        this.viewportWidth = width;
        this.syncMobileHeight();
        this.syncModeState();
        if (widthChanged) {
          this.endDrag();
          this.render({ immediate: true });
        }
      });
      this.resizeObserver.observe(this.viewport);
      this.desktopQuery.addEventListener('change', () => this.applyMode(), { signal: this.signal });
      this.loadDesktopSlides();
      this.go(0, { immediate: true, announce: false });
      document.fonts?.ready?.then(() => this.syncMobileHeight());
      window.PmSwipe?.prepareAdjacent(this.slides, this.index);
    }

    disconnectedCallback() {
      this.teardown();
    }

    teardown() {
      this.abortController?.abort();
      this.unbindSwipe?.();
      this.unbindSwipe = null;
      this.resizeObserver?.disconnect();
      this.abortController = null;
      this.resizeObserver = null;
      this.dragging = false;
    }

    matchesEditorEvent(event) {
      const target = event.target;
      return target === this || (target instanceof Node && (target.contains(this) || this.contains(target)));
    }

    loadSlide(index) {
      return window.PmSwipe?.prepareSlide(this.slides[index]);
    }

    get visibleCount() {
      return this.desktopQuery.matches ? 4 : 1;
    }

    get maxIndex() {
      return Math.max(0, this.slides.length - this.visibleCount);
    }

    loadDesktopSlides() {
      if (!this.desktopQuery.matches) return;
      this.slides.forEach((_, index) => this.loadSlide(index));
    }

    applyMode() {
      this.endDrag();
      this.loadDesktopSlides();
      this.syncMobileHeight();
      this.syncModeState();
      this.render({ immediate: true });
    }

    syncModeState() {
      this.index = Math.max(0, Math.min(this.maxIndex, this.index));
      this.dataset.activeIndex = String(this.index);
      const focused = document.activeElement;
      const focusedDot = this.dots.indexOf(focused);
      let hiddenSlideHadFocus = false;
      this.viewport.tabIndex = 0;
      this.viewport.setAttribute('aria-roledescription', 'carousel');
      this.slides.forEach((slide, index) => {
        const visible = index >= this.index && index < this.index + this.visibleCount;
        if (!visible && slide.contains(focused)) hiddenSlideHadFocus = true;
        slide.setAttribute('aria-hidden', String(!visible));
        slide.inert = !visible;
      });
      this.dots.forEach((dot, index) => {
        const selected = index === this.index;
        dot.hidden = index > this.maxIndex;
        dot.tabIndex = selected ? 0 : -1;
        if (selected) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (focusedDot > this.maxIndex) this.dots[this.index].focus({ preventScroll: true });
      else if (hiddenSlideHadFocus) this.viewport.focus({ preventScroll: true });
    }

    go(nextIndex, { immediate = false, announce = false } = {}) {
      const bounded = Math.max(0, Math.min(this.maxIndex, nextIndex));
      const changed = bounded !== this.index;
      this.index = bounded;
      this.loadSlide(this.index);
      window.PmSwipe?.prepareAdjacent(this.slides, this.index);
      this.dataset.activeIndex = String(this.index);

      this.syncModeState();

      this.syncMobileHeight();
      this.render({ immediate });
      Promise.resolve(this.loadSlide(this.index)).then(() => this.syncMobileHeight());
      if (announce && changed && this.status) {
        const title = this.slides[this.index].getAttribute('aria-label').replace(/^Reason \d+ of 6: /, '');
        this.status.textContent = this.desktopQuery.matches
          ? `Reasons ${this.index + 1}–${this.index + this.visibleCount} of 6`
          : `Reason ${this.index + 1} of 6: ${title}`;
      }
    }

    render({ immediate = false, dragOffset = 0 } = {}) {
      if (!this.track || !this.viewport) return;
      const noTransition = immediate || this.reducedMotion.matches || this.dragging;
      this.track.dataset.noTransition = String(noTransition);
      const firstSlide = this.slides[0];
      const slideWidth = firstSlide?.getBoundingClientRect().width || this.viewport.clientWidth;
      const trackStyles = getComputedStyle(this.track);
      const gap = parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
      const step = slideWidth + gap;
      const offset = -(this.index * step) + dragOffset;
      this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
    }

    syncMobileHeight() {
      if (!this.canvas || !this.viewport || !this.track || !this.dotRow) return;
      if (this.desktopQuery.matches) {
        this.canvas.style.removeProperty('height');
        this.viewport.style.removeProperty('height');
        this.track.style.removeProperty('height');
        this.slides.forEach((slide) => slide.style.removeProperty('height'));
        this.dotRow.style.removeProperty('top');
        return;
      }

      const card = this.slides[this.index];
      const body = card?.querySelector('.pm-home-reasons__body');
      const lastLine = body?.lastElementChild;
      if (!card || !lastLine) return;

      const cardRect = card.getBoundingClientRect();
      const image = card.querySelector('.pm-home-reasons__image');
      const title = card.querySelector('.pm-home-reasons__title');
      const contentBottom = Math.max(
        image?.getBoundingClientRect().bottom || cardRect.top,
        title?.getBoundingClientRect().bottom || cardRect.top,
        lastLine.getBoundingClientRect().bottom
      ) - cardRect.top;
      const cardHeight = Math.ceil(contentBottom + 24);
      const viewportTop = this.viewport.offsetTop;
      const dotsTop = viewportTop + cardHeight + 4;

      this.viewport.style.height = `${cardHeight}px`;
      this.track.style.height = `${cardHeight}px`;
      this.slides.forEach((slide) => { slide.style.height = `${cardHeight}px`; });
      this.dotRow.style.top = `${dotsTop}px`;
      this.canvas.style.height = `${dotsTop + 44}px`;
    }

    onKeydown(event) {
      let next = this.index;
      if (event.key === 'ArrowLeft') next = Math.max(0, this.index - 1);
      else if (event.key === 'ArrowRight') next = Math.min(this.maxIndex, this.index + 1);
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.maxIndex;
      else return;

      event.preventDefault();
      this.go(next, { announce: true });
    }

    onDotKeydown(event) {
      const current = Number(event.currentTarget.dataset.pmHomeReasonDot);
      let next = Number.isFinite(current) ? current : this.index;
      if (event.key === 'ArrowLeft') next = Math.max(0, next - 1);
      else if (event.key === 'ArrowRight') next = Math.min(this.maxIndex, next + 1);
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.maxIndex;
      else return;

      event.preventDefault();
      this.go(next, { announce: true });
      this.dots[next].focus({ preventScroll: true });
    }

    endDrag() {
      this.dragging = false;
      this.dragX = 0;
      delete this.viewport.dataset.dragging;
    }
  }

  customElements.define(elementName, PMHomeReasonsCarousel);
})();
